import {
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	InputAdornment,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import Papa from "papaparse";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import UsersTable from "src/components/profile/UsersTable";
import CustomProgressBarSummary from "src/components/users/CustomProgressBarSummary";
import UsersCountSummary from "src/components/users/UsersCountSummary";
import UsersSignupChart from "src/components/users/UsersSignupChart";
import { fCoins } from "src/utils/formatNumber";
import { get, post } from "src/utils/networkUtils";
import Iconify from "../components/iconify";

export default function UsersPage() {
	const [loading, setLoading] = useState(false);
	const [fixUsernamesLoading, setFixUsernamesLoading] = useState(false);
	const [rows, setRows] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const [stats, setStats] = useState(null);
	const [userLevelStats] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [marketingUsersDownloading, setMarketingUsersDownloading] = useState(false);

	useEffect(() => {
		get(`/admin/users/stats`).then((response) => {
			setStats(response);
		});
	}, []);

	useEffect(() => {
		const getUsers = setTimeout(() => {
			if (searchQuery) {
				setLoading(true);
				get(`/admin/users/search?query=${searchQuery}`).then((response) => {
					setRows(response.users);
					setLoading(false);
				});
			} else {
				get(`/admin/users?offset=0&limit=40`).then((response) => {
					setRows(response.users);
				});
			}
		}, 500);

		return () => clearTimeout(getUsers);
	}, [searchQuery]);

	const resetOnboarding = async () => {
		await post(`/admin/users/reset-onboard`);
	};

	async function downloadCSV(text) {
		const downloadelem = document.createElement("a");
		var blob = new Blob([text], { type: "text/csv;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		document.body.appendChild(downloadelem);
		downloadelem.href = url;
		downloadelem.download = "marketing-users.csv";
		downloadelem.click();
		downloadelem.remove();
		window.URL.revokeObjectURL(url);
	}

	const downloadMarketingUsers = async () => {
		setMarketingUsersDownloading(true);
		const users = await get("/admin/marketing-users");
		console.log(users);
		const csv = Papa.unparse(users);
		console.log(csv);
		downloadCSV(csv);
		setMarketingUsersDownloading(false);
	};

	const fixDeletedUsernames = async () => {
		setFixUsernamesLoading(true);
		try {
			await post(`/admin/users/fix-deleted-usernames`);
			// Optionally show success message or refresh data
		} catch (error) {
			setErrorMessage("Failed to fix deleted usernames");
		} finally {
			setFixUsernamesLoading(false);
		}
	};

	return (
		<>
			<Helmet>
				<title> Users </title>
			</Helmet>

			<Container>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="h4" gutterBottom>
						Users
					</Typography>
					<Stack direction="row" spacing={2}>
						<Button
							variant="outlined"
							onClick={downloadMarketingUsers}
							disabled={marketingUsersDownloading}
							startIcon={marketingUsersDownloading ? <CircularProgress size={20} /> : <Iconify icon="eva:arrow-circle-down-outline" />}
						>
							Marketing Users
						</Button>
						<Button
							variant="outlined"
							onClick={fixDeletedUsernames}
							disabled={fixUsernamesLoading}
							startIcon={fixUsernamesLoading ? <CircularProgress size={20} /> : <Iconify icon="eva:refresh-outline" />}
						>
							Fix Usernames
						</Button>
						<Button variant="contained" onClick={resetOnboarding}>
							Reset Onboarding
						</Button>
					</Stack>
				</Stack>
				{stats && (
					<Grid container spacing={1} sx={{ p: 0, mb: 1 }}>
						<Grid item xs={2}></Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="All Users" total={stats.usersCount ?? 0} />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Today" total={stats.newUsersCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="This Week" total={stats.newUsersThisWeekCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="This Month" total={stats.newUsersThisMonthCount ?? 0} color="info" />
						</Grid>

						<Grid item xs={2}></Grid>
						<Grid item xs={2}></Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Avg. Balance" total={fCoins(stats.avgBalance)} color="info" icon="/assets/icons/ufcoin.svg" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Yesterday" total={stats.newUsersYesterdayCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Last Week" total={stats.newUsersLastWeekCount ?? 0} color="info" />
						</Grid>
						<Grid item xs={2}>
							<UsersCountSummary title="Last Month" total={stats.newUsersLastMonthCount ?? 0} color="info" />
						</Grid>
					</Grid>
				)}
				{stats && (
					<Grid container spacing={1} sx={{ p: 0, mb: 2 }}>
						<Grid item xs={12} md={8} lg={8}>
							<UsersSignupChart
								title="Signups per Day"
								subheader=""
								chartLabels={stats.usersByDate.map((date) => date.date)}
								chartData={[
									{
										name: "Signups",
										type: "column",
										fill: "solid",
										data: stats.usersByDate.map((date) => date.userCount),
									},
								]}
							/>
						</Grid>
						<Grid item xs={12} md={4} lg={4}>
							{userLevelStats && (
								<CustomProgressBarSummary
									title="Player Value"
									sx={{ mb: 2 }}
									data={(() => {
										const maxUsers = Math.max(...userLevelStats.map((item) => item.userCount));
										return userLevelStats.map((item) => ({
											title: item.name,
											color: item.color,
											amount: item.userCount,
											pct: Math.round((item.userCount / maxUsers) * 80),
											store: 0,
										}));
									})()}
									ItemComponent={({ item }) => (
										<Chip
											label={item.title}
											sx={{
												backgroundColor: item.color,
												color: "white",
												fontWeight: "bold",
												minWidth: "50px",
												fontSize: "0.875rem",
												py: 1,
												mb: "2px",
											}}
										/>
									)}
								/>
							)}
						</Grid>
					</Grid>
				)}
				<Card>
					<Box sx={{ display: "flex", justifyContent: "center" }} style={{ padding: 20 }}>
						<TextField
							margin="dense"
							id="kickoff"
							label="Search"
							type="email"
							fullWidth
							variant="standard"
							onInput={(e) => {
								setSearchQuery(e.target.value);
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Iconify icon={"eva:search-outline"} />
									</InputAdornment>
								),
							}}
						/>
					</Box>
					{rows === null || loading ? (
						<Box sx={{ display: "flex", justifyContent: "center" }} style={{ padding: 20 }}>
							<CircularProgress />
						</Box>
					) : (
						<UsersTable users={rows} />
					)}
				</Card>
			</Container>

			<Dialog
				open={errorMessage !== null}
				onClose={() => setErrorMessage(null)}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">ERROR</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">{errorMessage}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setErrorMessage(null)}>OK</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
